import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Plug,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { APP_ROUTES, appRouteForPathname } from "@/lib/app-routes";
import {
  useAlerts,
  useDashboardSummary,
  useDashboardTrends,
  useHosts,
  useIntegrations,
} from "@/hooks/use-api";
import { formatRelativeTime, severityVariant } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { AlertSeverity, Integration } from "@/types/api";

const SEVERITY_CONFIG: Array<{
  key: AlertSeverity;
  label: string;
  valueClass: string;
  borderClass: string;
}> = [
  { key: "critical", label: "Critical", valueClass: "text-red-400", borderClass: "border-red-500/35" },
  { key: "high", label: "High", valueClass: "text-orange-400", borderClass: "border-orange-500/30" },
  { key: "medium", label: "Medium", valueClass: "text-yellow-400", borderClass: "border-yellow-500/30" },
  { key: "low", label: "Low", valueClass: "text-blue-400", borderClass: "border-blue-500/30" },
];

const CHART_COLORS = {
  critical: "#f87171",
  high: "#fb923c",
  medium: "#facc15",
  low: "#60a5fa",
};

export function SecurityOverview() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const summary = useDashboardSummary();
  const trends = useDashboardTrends(7);
  const priorityAlertQuery = useAlerts({ status: "open", sort: "priority", limit: 1 });
  const recentAlertsQuery = useAlerts({ sort: "newest", limit: 5 });
  const integrationQuery = useIntegrations();
  const hostQuery = useHosts();

  const needsReview = priorityAlertQuery.data?.alerts[0];
  const recentAlerts = recentAlertsQuery.data?.alerts ?? [];
  const appRoute = (route: string) => appRouteForPathname(location.pathname, route);
  const integrations = integrationQuery.data?.integrations ?? [];
  const healthySources = integrations.filter((integration) => integration.health_status === "healthy").length;
  const attentionAgents = (hostQuery.data?.summary.stale ?? 0) + (hostQuery.data?.summary.dead ?? 0);
  const coverage = integrations.length > 0
    ? Math.round((healthySources / integrations.length) * 100)
    : 0;
  const environmentUnavailable =
    (integrationQuery.isError && !integrationQuery.data) ||
    (hostQuery.isError && !hostQuery.data);
  const coverageLoading =
    (summary.isLoading && !summary.data) ||
    (integrationQuery.isLoading && !integrationQuery.data);
  const coverageUnavailable =
    (summary.isError && !summary.data) ||
    (integrationQuery.isError && !integrationQuery.data);
  const isRefreshing =
    summary.isFetching ||
    trends.isFetching ||
    priorityAlertQuery.isFetching ||
    recentAlertsQuery.isFetching ||
    integrationQuery.isFetching ||
    hostQuery.isFetching;

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["alerts"] });
    queryClient.invalidateQueries({ queryKey: ["integrations"] });
    queryClient.invalidateQueries({ queryKey: ["me", "hosts"] });
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-4 lg:space-y-5" data-testid="security-overview">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Security overview</h1>
          <p className="mt-1 text-sm text-muted-foreground">What needs attention across your security workspace</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={isRefreshing} className="self-start sm:self-auto">
          <RefreshCw className={cn("mr-1.5 h-4 w-4", isRefreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(330px,0.85fr)]">
        <section className="rounded-xl border border-red-500/35 bg-card p-4 sm:p-5" aria-labelledby="needs-review-title">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 id="needs-review-title" className="font-semibold">Needs review</h2>
              <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-semibold text-red-300">
                {priorityAlertQuery.isLoading && !priorityAlertQuery.data
                  ? "…"
                  : priorityAlertQuery.isError && !priorityAlertQuery.data
                    ? "—"
                    : needsReview
                      ? 1
                      : 0}
              </span>
            </div>
            <Link to={appRoute(APP_ROUTES.alerts)} className="text-xs font-medium text-primary hover:underline">
              View queue
            </Link>
          </div>

          {priorityAlertQuery.isLoading && !priorityAlertQuery.data ? (
            <div className="space-y-3 rounded-lg border border-border bg-[#0a0e13] p-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-9 w-48" />
            </div>
          ) : priorityAlertQuery.isError && !priorityAlertQuery.data ? (
            <PanelError
              message="Alert queue unavailable"
              onRetry={() => priorityAlertQuery.refetch()}
            />
          ) : needsReview ? (
            <div className="rounded-lg border border-border bg-[#0a0e13] p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={severityVariant(needsReview.severity)} className="capitalize">
                  {needsReview.severity}
                </Badge>
                {needsReview.enrichment_status === "complete" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary">
                    <Bot className="h-3.5 w-3.5" />
                    AI ready
                  </span>
                )}
              </div>
              <h3 className="mt-3 text-base font-semibold leading-snug sm:text-lg">{needsReview.title}</h3>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{sourceLabel(needsReview.source_type)}</span>
                <span className="inline-flex items-center gap-1">
                  <Clock3 className="h-3.5 w-3.5" />
                  {formatRelativeTime(needsReview.last_seen_at)}
                </span>
                <span className="capitalize">{needsReview.status}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`${appRoute(APP_ROUTES.alerts)}/${needsReview.id}`}
                  className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Open alert
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed border-border text-center">
              <CheckCircle2 className="h-7 w-7 text-primary" />
              <p className="mt-2 text-sm font-medium">No urgent alerts need review</p>
              <p className="mt-1 text-xs text-muted-foreground">The queue will surface the next priority here.</p>
            </div>
          )}
        </section>

        <EnvironmentHealth
          integrations={integrations}
          integrationsRoute={appRoute(APP_ROUTES.integrations)}
          isLoading={
            (integrationQuery.isLoading && !integrationQuery.data) ||
            (hostQuery.isLoading && !hostQuery.data)
          }
          isError={environmentUnavailable}
          healthySources={healthySources}
          attentionAgents={attentionAgents}
          onRetry={() => {
            integrationQuery.refetch();
            hostQuery.refetch();
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {SEVERITY_CONFIG.map((severity) => (
          <Link
            key={severity.key}
            to={`${appRoute(APP_ROUTES.alerts)}?severity=${severity.key}`}
            className={cn(
              "group rounded-xl border bg-card p-4 transition-colors hover:bg-secondary/70",
              severity.borderClass,
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{severity.label}</span>
              <ShieldCheck className={cn("h-4 w-4", severity.valueClass)} />
            </div>
            {summary.isLoading && !summary.data ? (
              <Skeleton className="mt-3 h-8 w-10" />
            ) : summary.isError && !summary.data ? (
              <p className="mt-2 text-3xl font-bold text-muted-foreground" aria-label={`${severity.label} alerts unavailable`}>
                —
              </p>
            ) : (
              <p className={cn("mt-2 text-3xl font-bold tabular-nums", severity.valueClass)}>
                {summary.data?.open_alerts[severity.key]}
              </p>
            )}
            <span className="text-xs text-muted-foreground group-hover:text-foreground">
              {summary.isError && !summary.data ? "Unavailable" : "Open alerts"}
            </span>
          </Link>
        ))}
      </div>

      <section className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground" aria-label="Coverage summary">
        {coverageLoading ? (
          <>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-28" />
          </>
        ) : coverageUnavailable ? (
          <div className="flex w-full items-center justify-between gap-3">
            <span>Coverage summary unavailable</span>
            <Button variant="ghost" size="sm" onClick={refresh}>Retry</Button>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Coverage <strong className="text-foreground">{coverage}%</strong>
            </span>
            <span className="hidden h-4 w-px bg-border sm:block" aria-hidden />
            <span className="inline-flex items-center gap-2">
              <Plug className="h-4 w-4" />
              <strong className="text-foreground">{integrations.length}</strong> connected sources
            </span>
            <span className="hidden h-4 w-px bg-border sm:block" aria-hidden />
            <span className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <strong className="text-foreground">{summary.data?.recent_alerts_24h ?? 0}</strong> alerts in 24h
            </span>
            <span className="hidden h-4 w-px bg-border sm:block" aria-hidden />
            <span className="inline-flex items-center gap-2">
              <CircleAlert className="h-4 w-4" />
              <strong className="text-foreground">{summary.data?.open_incidents ?? 0}</strong> open incidents
            </span>
          </>
        )}
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.8fr)]">
        <section className="min-w-0 rounded-xl border border-border bg-card p-4" aria-labelledby="alert-trend-title">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 id="alert-trend-title" className="font-semibold">Alert trend</h2>
              <p className="text-xs text-muted-foreground">Last seven days by severity</p>
            </div>
            <span className="rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground">7 days</span>
          </div>
          {trends.isLoading && !trends.data ? (
            <Skeleton className="h-64 w-full" />
          ) : trends.isError && !trends.data ? (
            <PanelError
              message="Alert trend unavailable"
              onRetry={() => trends.refetch()}
              className="h-64"
            />
          ) : trends.data?.trends?.length ? (
            <div className="h-64 w-full" role="img" aria-label="Seven-day alert trend chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.data.trends} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 15%)" />
                  <XAxis dataKey="date" stroke="#8a93a2" fontSize={11} tickLine={false} tickFormatter={shortDate} />
                  <YAxis stroke="#8a93a2" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0e1116",
                      border: "1px solid hsl(220 13% 20%)",
                      borderRadius: "0.625rem",
                      fontSize: "0.75rem",
                    }}
                    labelFormatter={(value) => shortDate(String(value))}
                  />
                  <Legend wrapperStyle={{ fontSize: "0.7rem" }} />
                  {(Object.keys(CHART_COLORS) as AlertSeverity[]).map((key) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key.charAt(0).toUpperCase() + key.slice(1)}
                      stroke={CHART_COLORS[key]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">No trend data available</div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-4" aria-labelledby="recent-activity-title">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 id="recent-activity-title" className="font-semibold">Recent activity</h2>
              <p className="text-xs text-muted-foreground">Latest alerts across connected sources</p>
            </div>
            <Link to={appRoute(APP_ROUTES.alerts)} className="text-xs font-medium text-primary hover:underline">View all</Link>
          </div>
          {recentAlertsQuery.isLoading && !recentAlertsQuery.data ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}
            </div>
          ) : recentAlertsQuery.isError && !recentAlertsQuery.data ? (
            <PanelError
              message="Recent activity unavailable"
              onRetry={() => recentAlertsQuery.refetch()}
              className="h-64"
            />
          ) : recentAlerts.length > 0 ? (
            <div className="divide-y divide-border">
              {recentAlerts.map((alert) => (
                <Link
                  key={alert.id}
                  to={`${appRoute(APP_ROUTES.alerts)}/${alert.id}`}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 py-3 text-sm hover:bg-secondary/40"
                >
                  <Badge variant={severityVariant(alert.severity)} className="capitalize">{alert.severity}</Badge>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{alert.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{sourceLabel(alert.source_type)}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatRelativeTime(alert.last_seen_at)}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">No recent alerts</div>
          )}
        </section>
      </div>

      {(summary.isError || trends.isError || priorityAlertQuery.isError || recentAlertsQuery.isError || integrationQuery.isError || hostQuery.isError) && (
        <div role="status" className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Some dashboard data could not be loaded. Available information may be stale while the dashboard retries.
        </div>
      )}
    </div>
  );
}

function EnvironmentHealth({
  integrations,
  integrationsRoute,
  isLoading,
  isError,
  healthySources,
  attentionAgents,
  onRetry,
}: {
  integrations: Integration[];
  integrationsRoute: string;
  isLoading: boolean;
  isError: boolean;
  healthySources: number;
  attentionAgents: number;
  onRetry: () => void;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5" aria-labelledby="environment-health-title">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 id="environment-health-title" className="font-semibold">Environment health</h2>
          <p className="text-xs text-muted-foreground">Coverage and source freshness</p>
        </div>
        <Link to={integrationsRoute} className="text-xs font-medium text-primary hover:underline">Manage</Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : isError ? (
        <PanelError message="Environment health unavailable" onRetry={onRetry} />
      ) : (
        <>
          <div className="mb-4 flex items-center gap-4 rounded-lg border border-border bg-[#0a0e13] p-3">
            <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[7px] border-primary/25 border-t-primary">
              <span className="text-lg font-bold text-primary">{healthySources}</span>
            </div>
            <div>
              <p className="text-sm font-semibold">{healthySources} / {integrations.length} sources healthy</p>
              <p className={cn("mt-1 text-xs", attentionAgents > 0 ? "text-amber-300" : "text-primary")}>
                {attentionAgents > 0
                  ? `${attentionAgents} agent${attentionAgents === 1 ? "" : "s"} need attention`
                  : "All enrolled agents current"}
              </p>
            </div>
          </div>
          <div className="divide-y divide-border">
            {integrations.slice(0, 5).map((integration) => {
              const healthy = integration.health_status === "healthy";
              return (
                <div key={integration.id} className="flex items-center gap-3 py-2 text-xs">
                  <span className={cn("h-2 w-2 rounded-full", healthy ? "bg-primary" : "bg-amber-400")} />
                  <span className="min-w-0 flex-1 truncate">{integration.display_name}</span>
                  <span className={healthy ? "text-primary" : "text-amber-300"}>
                    {healthy ? "Healthy" : integration.health_status === "unhealthy" ? "Needs attention" : integration.health_status}
                  </span>
                </div>
              );
            })}
            {integrations.length === 0 && (
              <div className="flex min-h-24 items-center justify-center text-sm text-muted-foreground">
                No connected sources
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function PanelError({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex min-h-40 flex-col items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 text-center",
        className,
      )}
    >
      <CircleAlert className="h-6 w-6 text-amber-300" />
      <p className="mt-2 text-sm font-medium">{message}</p>
      <p className="mt-1 text-xs text-muted-foreground">Check your connection and try again.</p>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function sourceLabel(source: string): string {
  const labels: Record<string, string> = {
    entra_id: "Entra ID",
    "entra_id + m365": "Entra ID + M365",
    m365: "Microsoft 365",
    endpoint: "Endpoint",
    google_workspace: "Google Workspace",
    cloudtrail: "AWS CloudTrail",
  };
  return labels[source] ?? source.replace(/_/g, " ");
}

function shortDate(value: string): string {
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const months = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[month] ?? parts[1]} ${day}`;
}
